import { Location } from 'history';

export type ScrollAnchor = {
  variantId: string;
  top: number;
  clicked: boolean;
};

export type ScrollPosition = {
  y: number;
  anchor?: ScrollAnchor;
};

type Entry = [key: string, position: ScrollPosition];

const STORAGE_KEY = 'scrollPositions';
const MAX_ENTRIES = 50;

export const CELL_SELECTOR = '.VariantCell[data-variant-pk], .PlaceholderCell[data-variant-pk]';

export const keyFor = (location: Location) =>
  location.key ?? `${location.pathname}${location.search}`;

const readEntries = (): Entry[] => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]');
  } catch {
    return [];
  }
};

export const getScrollPosition = (key: string) =>
  readEntries().find(([entryKey]) => entryKey === key)?.[1];

export const saveScrollPosition = (key: string, position: ScrollPosition) => {
  const entries = readEntries().filter(([entryKey]) => entryKey !== key);

  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify([...entries, [key, position]].slice(-MAX_ENTRIES))
    );
  } catch {
    // unavailable
  }
};

const isInViewport = (element: Element) => {
  const { top, bottom } = element.getBoundingClientRect();
  return bottom > 0 && top < window.innerHeight;
};

const isFullyBelowTop = (element: Element) => {
  const { top } = element.getBoundingClientRect();
  return top >= 0 && top < window.innerHeight;
};

const findAnchor = (clickedVariantId?: string): ScrollAnchor | undefined => {
  const cells = Array.from(document.querySelectorAll(CELL_SELECTOR));

  const clickedCell = cells.find(
    cell => cell.getAttribute('data-variant-pk') === clickedVariantId && isInViewport(cell)
  );
  const cell = clickedCell ?? cells.find(isFullyBelowTop);
  const variantId = cell?.getAttribute('data-variant-pk');

  if (!cell || !variantId) return;

  return { variantId, top: cell.getBoundingClientRect().top, clicked: Boolean(clickedCell) };
};

export const captureScrollPosition = (clickedVariantId?: string): ScrollPosition => ({
  y: window.scrollY,
  anchor: findAnchor(clickedVariantId)
});
